import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Tag, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  restoration: { badge: "CUSTOM BUILD", type: "Custom Build" },
  "3d_design": { badge: "3D DESIGN", type: "3D Design & Parts Fab" },
  coatings: { badge: "SPECIALTY COATINGS", type: "Specialty Coating" },
};

const getCategoryBadge = (category) =>
  (CATEGORY_LABELS[category] || CATEGORY_LABELS.coatings).badge;

const getCategoryType = (category) =>
  (CATEGORY_LABELS[category] || CATEGORY_LABELS.coatings).type;

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}`);
        setProject(response.data);
      } catch (err) {
        setError("Project not found");
        console.error("Error fetching project:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    if (!lightboxImage) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxImage]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen pt-20 bg-[#09090b] flex flex-col items-center justify-center">
        <h1 className="font-heading text-4xl text-white mb-4">PROJECT NOT FOUND</h1>
        <p className="font-body text-zinc-400 mb-8">The project you're looking for doesn't exist.</p>
        <Link to="/gallery">
          <Button className="bg-amber-500 text-black font-heading rounded-none">
            <ArrowLeft className="mr-2" size={20} />
            BACK TO GALLERY
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="project-detail-page">
      {/* Hero Image */}
      <section className="relative h-[60vh] sm:h-[70vh]">
        <img
          src={project.images?.[0] || "https://images.unsplash.com/photo-1581139196553-2d6c0c9ff634"}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-black/30 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/gallery"
          className="absolute top-24 left-4 sm:left-8 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          data-testid="back-to-gallery"
        >
          <ArrowLeft size={20} />
          <span className="font-mono text-sm">BACK TO GALLERY</span>
        </Link>
      </section>

      {/* Content */}
      <section className="relative -mt-32 pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-zinc-900 border border-white/10 p-8 sm:p-12"
          >
            {/* Category Badge */}
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-amber-500">
                <Tag size={14} />
                {getCategoryBadge(project.category)}
              </span>
              {project.year && (
                <span className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
                  <Calendar size={14} />
                  {project.year}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter mb-4">
              {project.title}
            </h1>

            {/* Vehicle Info */}
            {project.year && project.make && (
              <p className="font-body text-xl text-amber-500 mb-8">
                {project.year} {project.make} {project.model}
              </p>
            )}

            {/* Divider */}
            <div className="w-24 h-1 bg-amber-500 mb-8" />

            {/* Description */}
            <p className="font-body text-lg text-zinc-300 leading-relaxed mb-12">
              {project.description}
            </p>

            {/* Additional Images */}
            {project.images && project.images.length > 1 && (
              <div className="grid sm:grid-cols-2 gap-4 mb-12" data-testid="project-images">
                {project.images.slice(1).map((img, idx) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setLightboxImage(img)}
                    className="aspect-[4/3] overflow-hidden border border-white/10 bg-zinc-950 cursor-zoom-in group block w-full p-0"
                    aria-label={`View full image ${idx + 1}`}
                    data-testid={`project-thumb-${idx}`}
                  >
                    <img
                      src={img}
                      alt={`${project.title} — additional view ${idx + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Project Details */}
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <div className="p-6 bg-zinc-950 border border-white/10">
                <h3 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-2">
                  PROJECT TYPE
                </h3>
                <p className="font-heading text-xl text-white">
                  {getCategoryType(project.category)}
                </p>
              </div>
              <div className="p-6 bg-zinc-950 border border-white/10">
                <h3 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-2">
                  STATUS
                </h3>
                <p className="font-heading text-xl text-amber-500">
                  COMPLETED
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote">
                <Button 
                  className="bg-amber-500 text-black font-heading text-lg tracking-wide px-8 py-6 rounded-none hover:bg-amber-400 transition-all duration-200"
                  data-testid="start-your-project-btn"
                >
                  LET'S GET STARTED
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/gallery">
                <Button 
                  variant="outline"
                  className="border-white/20 text-white font-heading text-lg tracking-wide px-8 py-6 rounded-none hover:bg-white/5 transition-all duration-200"
                >
                  VIEW MORE PROJECTS
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox — rendered via portal at document.body to escape any ancestor transform/containing-block issues */}
      {lightboxImage && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex flex-col"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox-overlay"
        >
          {/* Header with close button */}
          <div className="relative z-10 flex justify-end p-4 sm:p-6 flex-shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
              className="flex items-center gap-2 px-4 py-3 bg-zinc-900 border border-white/20 text-white hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-colors font-mono text-xs sm:text-sm tracking-[0.2em] uppercase"
              aria-label="Close full-size image"
              data-testid="lightbox-close"
            >
              <X size={20} />
              <span>Close</span>
            </button>
          </div>
          {/* Image area — min-h-0 lets the flex item shrink */}
          <div className="flex-1 flex items-center justify-center px-4 pb-6 sm:px-8 sm:pb-8 min-h-0 overflow-auto">
            <img
              src={lightboxImage}
              alt={project.title}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain cursor-default"
              data-testid="lightbox-image"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
